(ns story-builder.event.request
  (:require
   [cljs.pprint :refer [pprint]]
   [goog.uri.utils :as uri]
   [graphql-query.core]
   [re-frame.core :as rf]
   [story-builder.util.core :as util]))

(defn- expand-on-response-hashmap [on-response]
  (into {}
        (mapcat (fn [[k v]]
                  (if (coll? k)
                    (mapv (fn [k] [(name k) v]) k)
                    [[(name k) v]])))
        on-response))

(def default-response-handlers
  "The response handlers used when no matching handler was provided."
  ;; More info at https://developer.mozilla.org/en-US/docs/Web/HTTP/Status
  (expand-on-response-hashmap
   {;; The status is 0 when a computer or its router is not connected to the Internet.
    :0xx {:outputs #{:alert :console}
          :error "Network error (cannot reach the server)"}

    ;; Successful response.
    :200 util/no-op

    [:1xx  ;; Informational responses.
     :2xx  ;; Successful responses.
     :3xx] ;; Redirection messages.

    {:outputs #{:console}}

    ;; Client error responses
    :402 {:logic   (fn [_response] (rf/dispatch [:billing/open-customer-portal]))
          :outputs #{}}
    :404 {:outputs #{:alert :console}
          :error "Network error (resource not found)"}
    :408 {:outputs #{:alert :console}
          :error "Network error (request time out)"}
    :4xx {:outputs #{:alert :console}
          :error (fn [{:keys [response]}]
                   (str "Client error (code " (:status response) ")"))}

    ;; Server error response
    :5xx {:outputs #{:alert :console}
          :error "Server error"}

    :xxx {:alert/type "danger"}}))

(defn- dispatch-request-response [response on-response default-response-handlers]
  (cond
    (fn? on-response)
    (on-response response)

    (vector? on-response)
    (let [[event-kw & event-params] on-response]
      (rf/dispatch (into [event-kw response] event-params)))

    (map? on-response)
    (let [response-handlers (expand-on-response-hashmap on-response)
          status (:status response)
          status-nxx (-> (str (quot status 100) "xx"))
          status-str (str status)
          handlers [(get response-handlers status-str)
                    (get response-handlers status-nxx)
                    (get response-handlers "xxx")
                    (get default-response-handlers status-str)
                    (get default-response-handlers status-nxx)
                    (get default-response-handlers "xxx")]
          first-handler (first (filter some? handlers))]
      (if (fn? first-handler)
        ;; Providing a function allows to override any default behavior for the matching status code.
        (first-handler response)
        ;; Else, only the hashmap handlers are considered and the behavior is a composition of logging and callback.
        (let [merged-handlers (->> (rseq handlers)
                                   (filter map?)
                                   (apply merge))

              ;; outputs is a set which can currently have the values in `#{:alert :console}`.
              ;; error can be a `(fn [{:keys [response]}]) -> string` or simply `string`.
              ;; alert/message can be a `(fn [{:keys [response error]}]) -> hiccup` or simply `hiccup`.
              ;; alert/type is the type of message for the alert, in `#{"danger" "warning" "success"}`.
              ;; logic is a `(fn [response])` or a vector `[:my-request/on-response param1 param2 ...]`.
              {outputs       :outputs
               error         :error
               logic         :logic
               alert-message :alert/message
               alert-type    :alert/type} merged-handlers

              error (if (fn? error)
                      (error {:response response})
                      error)

              alert-message (if (fn? alert-message)
                              (alert-message {:response response
                                              :error    error})
                              [:<>
                               (when alert-message [:p alert-message])
                               (when error [:p error])])]
          ;; Alert
          (when (contains? outputs :alert)
            (rf/dispatch [:ui/alert {:type    alert-type
                                     :message alert-message}]))
          ;; Console
          (when (contains? outputs :console)
            (pprint [:http/response (:status response) response]))

          ;; Logic
          (cond
            (fn? logic)
            (logic response)

            (vector? logic)
            (let [[event-kw & event-params] logic]
              (rf/dispatch (into [event-kw response] event-params)))))))))

(comment
  ;; Opens an alert.
  (rf/dispatch [:ui/alert {:type    "danger"
                           :message "alert-message"}])

  ;; The test playground for `dispatch-request-response`.
  ;; Come here to test the error dispatching/handling.
  (let [test-logic-callback (fn [response]
                              (pprint ["logic was called" response]))]
    (doseq [status [200 404 424]]
      (dispatch-request-response {:status status
                                  :content "My content"}
                                 {:200 (fn [response]
                                         (pprint [:200 response]))
                                  :xxx {:alert/message "An error occurred while cooking pasta."
                                        :logic         test-logic-callback}}
                                 default-response-handlers))))

(def current-ongoing-requests
  "Atom storing a map of request identifiers to core.async channels that
  can be used to cancel the request."
  (atom {}))

(rf/reg-event-fx
 :make-request
 (fn [{:keys [db]}
      [_ {:keys [authorize?
                 custom-opts
                 loader?
                 ;; The identifier for a local loader.
                 ;; It can be anything that can function as a key in a map.
                 ;; Disables the global loader implicitly.
                 local-loader
                 ;; If an equivalent request (same identifier) is ongoing,
                 ;; cancel it before making a new one.
                 cancel-ongoing?
                 method
                 on-response
                 default-on-response
                 response-body-parser
                 url]
          :or   {authorize?          true
                 loader?             true
                 on-response         {}
                 default-on-response default-response-handlers}
          :as   params}]]
   (let [{:keys [json-params
                 query-params
                 response-type
                 multipart-params
                 with-credentials?]
          :or   {with-credentials? true}} custom-opts
         access-token                   (get-in db [:local-storage :user :tokens :access])
         abort-controller               (new js/AbortController)
         loading-fn                     (if local-loader
                                          (fn [loading?]
                                            (if loading?
                                              (swap! current-ongoing-requests assoc local-loader abort-controller)
                                              (swap! current-ongoing-requests dissoc local-loader))
                                            (rf/dispatch [:ui/loading-local {:id local-loader :cancel abort-controller}]))
                                          (if loader?
                                            #(rf/dispatch [:ui/loading (assoc params :cancel abort-controller)])
                                            util/no-op))
         base-opts                      {:headers (merge
                                                   {"Content-Type" "application/json"
                                                    "Accept"       "application/json"}
                                                   (when (true? authorize?)
                                                     {"Authorization" (str "JWT " access-token)}))
                                         :method  method
                                         :signal  (.-signal abort-controller)}
         opts                           (merge-with merge base-opts custom-opts)
         opts                           (cond-> (assoc opts :credentials (if with-credentials? "include" "omit"))
                                          (= (get-in opts [:headers "Content-Type"]) "multipart/form-data")
                                          (update :headers dissoc "Content-Type")
                                          multipart-params
                                          (assoc :body (let [form-data (new js/FormData)]
                                                         (doseq [[k v] multipart-params]
                                                           (.append form-data (name k) v))
                                                         form-data))
                                          json-params
                                          (assoc :body (js/JSON.stringify (clj->js json-params))))]
     (when cancel-ongoing?
       (when-let [abort-controller (get @current-ongoing-requests local-loader)]
         (.abort abort-controller)
         (loading-fn false)))
     (when-not (contains? @current-ongoing-requests local-loader)
       (loading-fn true)
       (-> (js/fetch (uri/appendParamsFromMap url (clj->js query-params))
                     (clj->js opts))
           (.then (fn [r]
                    (-> (case response-type
                          :blob
                          (.blob (.clone r))
                          :text
                          (.text (.clone r))
                          (.json (.clone r)))
                        (.catch (fn [e]
                                  (js/console.error e)
                                  (.text r)))
                        (.then (fn [r2]
                                 (let [response {:body    (if (fn? response-body-parser)
                                                            (response-body-parser r2)
                                                            (js->clj r2 :keywordize-keys true))
                                                 :status  (.-status r)
                                                 :success (< (.-status r) 400)}]
                                   (loading-fn false)
                                   (dispatch-request-response response on-response default-on-response)))))))))

     ;; No re-frame effect returned
     {})))
